import { APIURL, appendFlatpickrBaseStyles, apiGet, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, initFlatpickrInShadow, toggleWidgetTheme, normalizeWidgetTheme, getEChartsTooltipTheme, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin } from '../../helpers';
import { MoverTypeDistributionConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

interface MoverTypeDistributionItem {
    mover_type: string;
    count: number;
    percentage: number;
    avg_change: number;
    avg_price: number;
    avg_relative_volume: number;
}

interface MoverTypeChartItem {
    name: string;
    value: number;
    count: number;
    avgChange: number;
    avgPrice: number;
    avgRelativeVolume: number;
}

const MOVER_TYPE_LABELS: Record<string, string> = {
    Loser: 'Losers',
    Gainer: 'Gainers',
    MostActive: 'Most Active'
};

const MOVER_TYPE_COLORS: Record<string, string> = {
    Losers: '#FF2B2B',
    Gainers: '#6FF19A',
    'Most Active': '#8B6CFF'
};

export class TraderverseMarketDistributionMoverType {
    private container: HTMLElement;
    private config: MoverTypeDistributionConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private shadowRoot: ShadowRoot;
    private currentData: MoverTypeChartItem[] = [];
    private currentChart: any;
    private widgetResizeObserver: ResizeObserver | null = null;
    private filters = {
        dateFilter: '',
        symbolFilter: '' as string | undefined,
        minChange: '' as number | '',
        maxChange: '' as number | '',
        minPrice: '' as number | '',
        maxPrice: '' as number | '',
        minRelativeVolume: '' as number | '',
        maxRelativeVolume: '' as number | ''
    };

    constructor(config: MoverTypeDistributionConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.applyConfigFilters();
        this.container = this.resolveContainer(config.container);
        this.shadowRoot = (this.container as any).shadowRoot || this.container.attachShadow({ mode: 'closed' });
        this.init();
        this.widgetResizeObserver = new ResizeObserver(() => this.updateDisplay());
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private getDefaultConfig(): Partial<MoverTypeDistributionConfig> {
        return {
      headerPinned: true,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true,
            isSymbolSelectable: true,
            symbol: 'AAPL',
            symbolFilter: 'AAPL',
            dateFilter: new Date().toISOString().slice(0, 10)
        };
    }

    private applyConfigFilters(): void {
        if (this.config.dateFilter) this.filters.dateFilter = this.config.dateFilter;
        if (this.config.symbolFilter) {
            this.filters.symbolFilter = this.config.symbolFilter;
            this.config.symbol = this.config.symbolFilter;
        } else if (this.config.symbol) {
            this.filters.symbolFilter = this.config.symbol;
        }
        if (typeof this.config.minChange === 'number') this.filters.minChange = this.config.minChange;
        if (typeof this.config.maxChange === 'number') this.filters.maxChange = this.config.maxChange;
        if (typeof this.config.minPrice === 'number') this.filters.minPrice = this.config.minPrice;
        if (typeof this.config.maxPrice === 'number') this.filters.maxPrice = this.config.maxPrice;
        if (typeof this.config.minRelativeVolume === 'number') this.filters.minRelativeVolume = this.config.minRelativeVolume;
        if (typeof this.config.maxRelativeVolume === 'number') this.filters.maxRelativeVolume = this.config.maxRelativeVolume;
        if (!this.filters.dateFilter) {
            this.filters.dateFilter = new Date().toISOString().slice(0, 10);
        }
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private async init(): Promise<void> {
        await this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        await this.setupChart();
        if (widget) widget.classList.remove('loading');
        cancelSelectEvent(this.shadowRoot);
        this.emit('ready', {});
        this.config.onReady?.();
    }

    private async createWidget(): Promise<void> {
        let themeClass = 'defaultDarkMode';
        let dataTheme = 'default-dark';

        if (this.config.theme === 'white') {
            themeClass = 'whiteMode';
            dataTheme = 'white';
        } else if (this.config.theme === 'black') {
            themeClass = 'blackMode';
            dataTheme = 'black';
        } else if (this.config.theme === 'default-lite') {
            themeClass = 'defaultLightMode';
            dataTheme = 'default-lite';
        }

        const widgetHtml = `
        <div style="width: ${typeof this.config.width == "number" ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == "number" ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
            <div class="headTop">
                <h4 class="title market">${this.getTitleIconSvg()} Distribution of mover Type</h4>
                <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
                    <div class="item">
                        <button class="filters-toggle" type="button">
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                            </svg>
                        </button>
                    </div>
                    <i class="pi pi-ellipsis-v"></i>
                </div>
            </div>
            <div class="inBody">
                <div class="optionsRow">
                    <div class="item dateRange">
                        <input type="text" class="date-input" readonly />
                        <i><svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.91667 1.16667H8.75V0.583333C8.75 0.428624 8.68854 0.280251 8.57915 0.170854C8.46975 0.0614581 8.32138 0 8.16667 0C8.01196 0 7.86358 0.0614581 7.75419 0.170854C7.64479 0.280251 7.58333 0.428624 7.58333 0.583333V1.16667H4.08333V0.583333C4.08333 0.428624 4.02188 0.280251 3.91248 0.170854C3.80308 0.0614581 3.65471 0 3.5 0C3.34529 0 3.19692 0.0614581 3.08752 0.170854C2.97813 0.280251 2.91667 0.428624 2.91667 0.583333V1.16667H1.75C1.28587 1.16667 0.840752 1.35104 0.512563 1.67923C0.184375 2.00742 0 2.45254 0 2.91667V9.91667C0 10.3808 0.184375 10.8259 0.512563 11.1541C0.840752 11.4823 1.28587 11.6667 1.75 11.6667H9.91667C10.3808 11.6667 10.8259 11.4823 11.1541 11.1541C11.4823 10.8259 11.6667 10.3808 11.6667 9.91667V2.91667C11.6667 2.45254 11.4823 2.00742 11.1541 1.67923C10.8259 1.35104 10.3808 1.16667 9.91667 1.16667ZM10.5 9.91667C10.5 10.0714 10.4385 10.2198 10.3291 10.3291C10.2198 10.4385 10.0714 10.5 9.91667 10.5H1.75C1.59529 10.5 1.44692 10.4385 1.33752 10.3291C1.22812 10.2198 1.16667 10.0714 1.16667 9.91667V5.83333H10.5V9.91667ZM10.5 4.66667H1.16667V2.91667C1.16667 2.76196 1.22812 2.61358 1.33752 2.50419C1.44692 2.39479 1.59529 2.33333 1.75 2.33333H2.91667V2.91667C2.91667 3.07138 2.97813 3.21975 3.08752 3.32915C3.19692 3.43854 3.34529 3.5 3.5 3.5C3.65471 3.5 3.80308 3.43854 3.91248 3.32915C4.02188 3.21975 4.08333 3.07138 4.08333 2.91667V2.33333H7.58333V2.91667C7.58333 3.07138 7.64479 3.21975 7.75419 3.32915C7.86358 3.43854 8.01196 3.5 8.16667 3.5C8.32138 3.5 8.46975 3.43854 8.57915 3.32915C8.68854 3.21975 8.75 3.07138 8.75 2.91667V2.33333H9.91667C10.0714 2.33333 10.2198 2.39479 10.3291 2.50419C10.4385 2.61358 10.5 2.76196 10.5 2.91667V4.66667Z" fill="#9F9FA2"/></svg></i>
                    </div>
                    <span></span>
                    <div class="filtersBox sideBar hidden">
                        ${this.config.hideSymbol ? '' : `<div class="item dropStyle01">
                            <div class="trader-sm-card" id="selected-symbol">
                                ${this.config.symbol
                                    ? getTraderSMCardHTML(this.config.stock ?? { symbol: this.config.symbol, type: 'stock', name: '' })
                                    : '<span>All Symbols</span>'}
                            </div>
                            <span id="symbol-dropdown-placeholder"></span>
                        </div>`}
                        <div class="item">
                            <label>Min Change %</label>
                            <input type="number" class="filter-min-change" step="0.01" placeholder="e.g. -10" value="${this.filters.minChange}" />
                        </div>
                        <div class="item">
                            <label>Max Change %</label>
                            <input type="number" class="filter-max-change" step="0.01" placeholder="e.g. 100" value="${this.filters.maxChange}" />
                        </div>
                        <div class="item">
                            <label>Min Price</label>
                            <input type="number" class="filter-min-price" step="0.01" placeholder="e.g. 5" value="${this.filters.minPrice}" />
                        </div>
                        <div class="item">
                            <label>Max Price</label>
                            <input type="number" class="filter-max-price" step="0.01" placeholder="e.g. 500" value="${this.filters.maxPrice}" />
                        </div>
                        <div class="item">
                            <label>Min Relative Volume</label>
                            <input type="number" class="filter-min-relative-volume" step="0.01" placeholder="e.g. 1" value="${this.filters.minRelativeVolume}" />
                        </div>
                        <div class="item">
                            <label>Max Relative Volume</label>
                            <input type="number" class="filter-max-relative-volume" step="0.01" placeholder="e.g. 10000" value="${this.filters.maxRelativeVolume}" />
                        </div>
                    </div>
                </div>
                <div class="chartDv cryptoCompany" id="portfolioAssetTypeContainer"></div>
            </div>
        </div>`;
        this.shadowRoot.innerHTML = widgetHtml;
        await this.attachStyles();
        this.setupEventListeners();
    }

    private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
        const filtersToggle = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement;
        const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement;
        if (filtersToggle && filtersBox) {
            filtersToggle.addEventListener('click', (e) => {
                e.stopPropagation();
                filtersBox.classList.toggle('hidden');
            });
        }

        if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
            const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
            const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
            if (placeholder && dropdown) {
                placeholder.replaceWith(dropdown);
            }
        }

        const dateInput = this.shadowRoot.querySelector('.date-input') as HTMLInputElement;
        if (dateInput) {
            dateInput.value = this.filters.dateFilter;
            initFlatpickrInShadow(dateInput, this.shadowRoot, {
                mode: 'single',
                dateFormat: 'Y-m-d',
                defaultDate: this.filters.dateFilter,
                onChange: (selectedDates: Date[]) => {
                    if (selectedDates.length === 1) {
                        this.filters.dateFilter = selectedDates[0].toISOString().slice(0, 10);
                        dateInput.value = this.filters.dateFilter;
                        this.setupChart();
                    }
                }
            });
        }

        const updateNumericFiltersAndFetch = () => {
            this.filters.minChange = this.readNumberInput('.filter-min-change');
            this.filters.maxChange = this.readNumberInput('.filter-max-change');
            this.filters.minPrice = this.readNumberInput('.filter-min-price');
            this.filters.maxPrice = this.readNumberInput('.filter-max-price');
            this.filters.minRelativeVolume = this.readNumberInput('.filter-min-relative-volume');
            this.filters.maxRelativeVolume = this.readNumberInput('.filter-max-relative-volume');
            this.setupChart();
        };

        [
            '.filter-min-change',
            '.filter-max-change',
            '.filter-min-price',
            '.filter-max-price',
            '.filter-min-relative-volume',
            '.filter-max-relative-volume'
        ].forEach((selector) => {
            const input = this.shadowRoot.querySelector(selector) as HTMLInputElement | null;
            input?.addEventListener('change', updateNumericFiltersAndFetch);
        });
    }

    private readNumberInput(selector: string): number | '' {
        const input = this.shadowRoot.querySelector(selector) as HTMLInputElement | null;
        if (!input || input.value.trim() === '') return '';
        const value = parseFloat(input.value);
        return Number.isNaN(value) ? '' : value;
    }

    private onSelectChange(stock: any): void {
        this.changeSymbol(stock.symbol, stock);
    }

    private buildQueryParams(): Record<string, string | number> {
        const query: Record<string, string | number> = {};
        if (this.filters.dateFilter) query.dateFilter = this.filters.dateFilter;
        if (this.filters.symbolFilter) query.symbolFilter = this.filters.symbolFilter;
        if (this.filters.minChange !== '') query.minChange = this.filters.minChange;
        if (this.filters.maxChange !== '') query.maxChange = this.filters.maxChange;
        if (this.filters.minPrice !== '') query.minPrice = this.filters.minPrice;
        if (this.filters.maxPrice !== '') query.maxPrice = this.filters.maxPrice;
        if (this.filters.minRelativeVolume !== '') query.minRelativeVolume = this.filters.minRelativeVolume;
        if (this.filters.maxRelativeVolume !== '') query.maxRelativeVolume = this.filters.maxRelativeVolume;
        return query;
    }

    private mapApiItem(item: MoverTypeDistributionItem): MoverTypeChartItem {
        const name = MOVER_TYPE_LABELS[item.mover_type] || item.mover_type;
        return {
            name,
            value: item.percentage,
            count: item.count,
            avgChange: item.avg_change,
            avgPrice: item.avg_price,
            avgRelativeVolume: item.avg_relative_volume
        };
    }

    private async attachStyles(): Promise<void> {
        try {
            appendFlatpickrBaseStyles(this.shadowRoot);
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
            script.async = true;
            script.onload = () => resolve(true);
            this.shadowRoot.appendChild(script);
        });
    }

    private async setupChart(symbol?: string): Promise<void> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const container = this.shadowRoot.querySelector('#portfolioAssetTypeContainer');
        if (widget) widget.classList.add('loading');

        if (symbol) {
            this.filters.symbolFilter = symbol;
            this.config.symbol = symbol;
        }

        try {
            const res = await apiGet({
                method: 'GET',
                url: `${APIURL}/v1/equity-market/mover-type-distribution`,
                query: this.buildQueryParams(),
                headers: { }
            });
            if (!res.ok) throw new Error('Failed to fetch mover type distribution data');

            const json = await res.json();
            const items = Array.isArray(json?.data?.data) ? json.data.data as MoverTypeDistributionItem[] : [];
            this.currentData = items.map((item) => this.mapApiItem(item));

            if (!this.currentData.length) {
                if (container) container.innerHTML = '<div class="noData"><p>No data available</p></div>';
                if (this.currentChart) {
                    this.currentChart.dispose();
                    this.currentChart = null;
                }
            } else {
                this.renderPortfolioChart();
            }

            this.emit('dataUpdate', json?.data ?? null);
        } catch (err) {
            console.error('Failed to fetch/setup chart data:', err);
            this.currentData = [];
            if (container) container.innerHTML = '<div class="noData"><p>Failed to load data</p></div>';
            this.emit('error', err);
        } finally {
            if (widget) widget.classList.remove('loading');
        }
    }

    private updateDisplay(): void {
        if (!this.currentData?.length || !(window as any).echarts) {
            return;
        }
        this.renderPortfolioChart();
    }

    private renderPortfolioChart(): void {
        const container = this.shadowRoot.querySelector('#portfolioAssetTypeContainer');
        if (!container) return;
        if (this.currentData && this.currentData.length > 0) {
    container?.setAttribute('data-testing-id', 'mover-type-chart');
} else {
    container?.removeAttribute('data-testing-id');
}

        container.innerHTML = `<div id="portfolioChart" class="chart coverDv"></div>`;
        this.drawChart();
    }

    private drawChart(): void {
        const echarts = (window as any).echarts;
        const container = this.shadowRoot.querySelector('#portfolioChart') as HTMLElement;
        if (!container || !this.currentData?.length) return;

        if (this.currentChart) {
            this.currentChart.dispose();
        }

        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const fontSize = getComputedStyle(widgetBox).getPropertyValue('--font-size-sm');
        const bgColor = getComputedStyle(widgetBox).getPropertyValue('--base-dark');

        const option = {
            backgroundColor: 'transparent',
            tooltip: {
                trigger: 'item',
                ...getEChartsTooltipTheme(widgetBox),
                formatter: (p: any) => `
                  <div style="min-width:160px">
                    <div style="font-weight:600;margin-bottom:6px">
                      <span style="
                        display:inline-block;
                        width:10px;
                        height:10px;
                        border-radius:50%;
                        background:${p.color};
                        margin-right:6px
                      "></span>
                      ${p.name}
                    </div>
                    <div>Count <b>${p.data.count}</b></div>
                    <div>${p.value.toFixed(1)}% Percentage</div>
                    <div>${p.data.avgChange.toFixed(2)}% Avg Change</div>
                    <div>$${p.data.avgPrice.toFixed(2)} Avg Price</div>
                    <div>${p.data.avgRelativeVolume.toFixed(2)} Avg Relative Volume</div>
                  </div>
                `
            },
            series: [
                {
                    type: 'pie',
                    radius: ['30%', '90%'],
                    center: ['50%', '55%'],
                    startAngle: 90,
                    minAngle: 6,
                    avoidLabelOverlap: true,
                    padAngle: 3,
                    itemStyle: {
                        borderRadius: 8,
                        borderColor: bgColor,
                        borderWidth: 6
                    },
                    label: {
                        show: true,
                        position: 'inside',
                        formatter: '{b}\n{d}%',
                        color: '#FFFFFF',
                        fontSize: fontSize
                    },
                    labelLine: { show: false },
                    data: this.currentData.map((d) => ({
                        ...d,
                        itemStyle: { color: MOVER_TYPE_COLORS[d.name] || '#8B6CFF' }
                    }))
                }
            ]
        };

        this.currentChart = echarts.init(container);
        this.currentChart.setOption(option);
    }

    private changeSymbol(stockId: string, stock: any = null): void {
        changeSymbolForWidget(
            this.shadowRoot,
            this.config,
            stock,
            stockId,
            (symbol: string) => this.setupChart(symbol),
            (type: any, data: any) => this.emit(type, data)
        );
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }

    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }

    public setSymbol(symbol: string): void {
        this.changeSymbol(symbol);
        const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        if (dropdown) dropdown.value = symbol;
    }

    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }

    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
        if (!this.shadowRoot) return;

        this.config.theme = toggleWidgetTheme(
            this.shadowRoot,
            theme
        ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';

        this.updateDisplay();
    }

    public destroy(): void {
        if (this.currentChart) {
            this.currentChart.dispose();
            this.currentChart = null;
        }
        if (this.widgetResizeObserver) {
            this.widgetResizeObserver.disconnect();
            this.widgetResizeObserver = null;
        }
        this.eventHandlers.clear();
        this.shadowRoot.innerHTML = '';
    }

    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) {
            const evt: WidgetEvent = { type, data };
            handlers.forEach(h => h(evt));
        }
    }

    private getTitleIconSvg(): string {
        return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }
}

declare global { interface Window { TraderverseMarketDistributionMoverType: typeof TraderverseMarketDistributionMoverType; } }
if (typeof window !== 'undefined') window.TraderverseMarketDistributionMoverType = TraderverseMarketDistributionMoverType;
